import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { GitBranch } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';

const dataAustralia = [
  { name: "Panel / Consortium", value: 33.4, color: "#0A1A2F" },
  { name: "Direct purchase", value: 29.7, color: "#1C485C" },
  { name: "In-house / OSS", value: 16.2, color: "#3D8C91" },
  { name: "Unknown", value: 4.9, color: "#D1D5DB" },
  { name: "Pilot / POC", value: 4.8, color: "#56B0AF" },
  { name: "Bundled", value: 4.5, color: "#89CDD4" },
  { name: "Legacy / Perpetual", value: 3.0, color: "#C6F2F0" },
  { name: "Partner SaaS", value: 2.3, color: "#56B0AF" },
  { name: "Competitive tender", value: 1.3, color: "#3D8C91" }
];

const dataSouthAfrica = [
  { name: "Not Disclosed / Unknown", value: 61.5, color: "#D1D5DB" },
  { name: "Direct Award / Single Source", value: 22.3, color: "#1C485C" },
  { name: "In-house / OSS", value: 9.2, color: "#3D8C91" },
  { name: "Competitive Procedures", value: 3.8, color: "#0A1A2F" },
  { name: "Framework / GPO", value: 2.3, color: "#56B0AF" },
  { name: "RFI / Market Sounding", value: 0.8, color: "#89CDD4" }
];

const dataSkillwell = [
  { name: "BU-led pilots", value: 28.6, color: "#0A1A2F" },
  { name: "Division-led pilots", value: 14.3, color: "#1C485C" },
  { name: "Function-led pilots", value: 14.3, color: "#3D8C91" },
  { name: "Technical pilot", value: 7.1, color: "#56B0AF" },
  { name: "Site BU pilot", value: 7.1, color: "#89CDD4" },
  { name: "BU sponsor RFP/Sourcing", value: 14.3, color: "#C6F2F0" },
  { name: "Subcontracting", value: 7.1, color: "#D1D5DB" },
  { name: "Org pilot portal", value: 7.1, color: "#00C2C2" }
];

const dataKairadigital = [
  { name: "Function-led pilots", value: 70.0, color: "#0A1A2F" },
  { name: "Division-led pilots", value: 10.0, color: "#1C485C" },
  { name: "Technical pilot", value: 10.0, color: "#3D8C91" },
  { name: "RFP/Sourcing", value: 10.0, color: "#56B0AF" }
];

const dataLearnwise = [
  { name: "Framework / consortium / panel", value: 61.0, color: "#0A1A2F" },
  { name: "Direct / sole-source / limited tender", value: 16.9, color: "#1C485C" },
  { name: "Renewal / extension / support continuation", value: 13.6, color: "#3D8C91" },
  { name: "Open competition / RFP", value: 5.1, color: "#56B0AF" },
  { name: "Select / invited procurement", value: 3.4, color: "#89CDD4" }
];

const procurementPortalsWayground = [
  { entity: "Medellín – Secretaría de Educación", portal: "SECOP II Licitaciones", authority: "Alcaldía de Medellín" },
  { entity: "UGEL 03 – Lima Metropolitana", portal: "SEACE Convocatorias", authority: "" },
  { entity: "Antioquia – Secretaría de Educación", portal: "SECOP II Licitaciones", authority: "" },
  { entity: "Yucatán – SEGEY / CEEY", portal: "Adquisiciones Yucatán", authority: "" },
  { entity: "Nuevo León – Secretaría de Educación", portal: "NL Licitaciones Públicas", authority: "" },
  { entity: "CABA – Ministerio de Educación", portal: "Buenos Aires Compras", authority: "" },
  { entity: "Chihuahua – SEECh", portal: "SEECH Licitaciones", authority: "" },
  { entity: "Bogotá – Secretaría de Educación", portal: "SECOP I/II Licitaciones", authority: "" },
  { entity: "Baja California – Secretaría de Educación", portal: "Plataforma Compras BC", authority: "" },
  { entity: "Jalisco – Secretaría de Educación", portal: "Compras Jalisco", authority: "" },
  { entity: "Plan Ceibal / ANEP", portal: "Compras Estatales", authority: "" },
  { entity: "SLEP Atacama", portal: "Mercado Público", authority: "" }
];

const procurementPortalsPlataformaa = [
  { entity: "Universidad Peruana de Ciencias Aplicadas (UPC)", portal: "Private internal procurement", country: "Peru" },
  { entity: "Universidad Autónoma del Estado de México (UAEMéx)", portal: "University public tenders", country: "Mexico" },
  { entity: "Benemérita Universidad Autónoma de Puebla (BUAP)", portal: "University public tenders", country: "Mexico" },
  { entity: "Universidad Nacional de San Cristóbal de Huamanga (UNSCH)", portal: "SEACE public procurement", country: "Peru" },
  { entity: "Universidad de Guadalajara (UdeG)", portal: "University public tenders", country: "Mexico" },
  { entity: "Universidad Nacional Abierta y a Distancia (UNAD)", portal: "SECOP II tenders", country: "Colombia" },
  { entity: "Politécnico Colombiano Jaime Isaza Cadavid", portal: "SECOP II tenders", country: "Colombia" },
  { entity: "Universidad Distrital Francisco José de Caldas", portal: "SECOP I / II", country: "Colombia" },
  { entity: "Universidad de Cundinamarca", portal: "SECOP II tenders", country: "Colombia" },
  { entity: "Universidad de La Frontera (UFRO)", portal: "Mercado Público tenders", country: "Chile" },
  { entity: "Universidad de Talca", portal: "Mercado Público tenders", country: "Chile" },
  { entity: "Universidad Nacional Autónoma de México (UNAM)", portal: "Public tenders / invitations", country: "Mexico" },
  { entity: "Universidad Nacional del Centro del Perú (UNCP)", portal: "SEACE public procurement", country: "Peru" },
  { entity: "Universidad Nacional de Colombia (UNAL)", portal: "SECOP II tenders", country: "Colombia" }
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const fullNames = {
      "Panel / Consortium": "Panel / Consortium call-off",
      "Partner SaaS": "Partner-implemented SaaS",
      "Bundled": "Bundled / Included",
      "Unknown": "Unknown / Not stated",
      "Pilot / POC": "Pilot / POC / Emergency",
      "Not Disclosed / Unknown": "Not disclosed / Unknown",
      "Direct Award / Single Source": "Direct Award / Single Source / Sole Supplier",
      "In-house / OSS": "In-house / Open-Source / OSS",
      "Competitive Procedures": "Open Tender / Restricted / Competitive",
      "Framework / GPO": "Framework / GPO / Cooperative Purchasing",
      "RFI / Market Sounding": "RFI / Market Sounding (pre-RFP)",
      "BU-led pilots": "Business Unit-led pilot programs",
      "Division-led pilots": "Division-led pilot programs",
      "Function-led pilots": "Function-led pilot programs",
      "Technical pilot": "Technical pilot / POC",
      "Site BU pilot": "Site-level Business Unit pilot",
      "BU sponsor RFP/Sourcing": "BU sponsor RFP or sourcing process",
      "Subcontracting": "Subcontracting routes",
      "Org pilot portal": "Organization-wide pilot portal"
    };
    return (
      <div className="bg-[#0A1A2F] text-white p-3 rounded-lg shadow-lg max-w-xs border border-[#00C2C2]">
        <p className="font-semibold text-sm mb-1">{fullNames[data.name] || data.name}</p>
        <p className="text-xs text-[#00C2C2]">{data.value}% of procurements</p>
      </div>
    );
  }
  return null;
};

export default function ProcurementRoutes({ market }) {
  const { t } = useTranslation();
  const isSouthAfrica = market === 'south-africa';
  const isSkillwell = market === 'skillwell';
  const isKairadigital = market === 'kairadigital';
  const isWayground = market === 'wayground';
  const isPlataformaa = market === 'plataformaa';
  const isLearnwise = market === 'learnwise';
  
  let data, commentary;
  
  if (isPlataformaa) {
    // Display procurement portals as a table for plataformaa
    return (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-[#00C2C2]" />
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('procurementRoutes')}</CardTitle>
          </div>
          <p className="text-sm text-[#5C6B7A]">{t('howInstitutionsBuy')}</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-semibold text-[#0A1A2F]">Account</th>
                  <th className="text-left py-2 px-3 font-semibold text-[#0A1A2F]">Country</th>
                  <th className="text-left py-2 px-3 font-semibold text-[#0A1A2F]">Typical procurement route</th>
                </tr>
              </thead>
              <tbody>
                {procurementPortalsPlataformaa.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 text-[#0A1A2F]">{item.entity}</td>
                    <td className="py-2 px-3 text-[#5C6B7A]">{item.country}</td>
                    <td className="py-2 px-3 text-[#5C6B7A]">{item.portal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-[#5C6B7A] leading-relaxed">
              University procurement in Latin America is dominated by public tender platforms: Mexico universities use institution-level public tenders (UNAM, UdeG, UAEMéx, BUAP), Colombia routes through SECOP I/II centralized system (5 universities), Peru uses SEACE portal (UNSCH, UNCP), and Chile operates via Mercado Público (UFRO, Universidad de Talca). Only UPC uses private internal procurement. All public universities mandate formal tender processes with significant documentation and compliance requirements, creating high barriers to entry for international vendors unfamiliar with country-specific platforms.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (isWayground) {
    // Display procurement portals as a table instead of pie chart
    return (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-[#00C2C2]" />
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('procurementRoutes')}</CardTitle>
          </div>
          <p className="text-sm text-[#5C6B7A]">{t('howInstitutionsBuy')}</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-semibold text-[#0A1A2F]">Education Authority</th>
                  <th className="text-left py-2 px-3 font-semibold text-[#0A1A2F]">Procurement Portal</th>
                </tr>
              </thead>
              <tbody>
                {procurementPortalsWayground.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 text-[#0A1A2F]">{item.entity}</td>
                    <td className="py-2 px-3 text-[#5C6B7A]">{item.portal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-[#5C6B7A] leading-relaxed">
              Public sector procurement in Latin America is predominantly governed by mandatory electronic tender platforms. Each state or municipal education authority operates its own procurement portal (SECOP in Colombia, SEACE in Peru, Compras Estatales in Uruguay, Mercado Público in Chile, etc.), with all tenders published electronically and subject to strict regulatory frameworks. Most procurements follow open tender procedures with limited flexibility for direct awards.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (isKairadigital) {
    data = dataKairadigital;
    commentary = "Enterprise procurement across these global companies is overwhelmingly function-led (70%), with business functions driving pilot programs and vendor selection. Division-led, technical pilots, and formal RFP/sourcing each represent 10% of routes. This landscape strongly favors working with functional champions to establish proof-of-value before broader organizational rollout.";
  } else if (isLearnwise) {
    data = dataLearnwise;
    commentary = "This analysis shows that buyers often rely on established purchasing routes, especially frameworks and consortium arrangements, rather than launching a full open tender each time. Direct awards and renewals are also common, which points to the importance of existing relationships and accessible buying routes. In practice, success in this market often depends as much on being easy to buy as on competing in a formal procurement process.";
  } else if (isSkillwell) {
    data = dataSkillwell;
    commentary = "Enterprise procurement routes across these Fortune 500 accounts are predominantly pilot-driven: BU-led pilots (29%) lead the way, followed by division and function-led pilots (14% each). Technical and site-level pilots provide targeted entry points (7% each), while formal RFP/sourcing and subcontracting routes account for the remaining opportunities. This landscape favors agile, champion-driven approaches over traditional procurement.";
  } else if (isSouthAfrica) {
    data = dataSouthAfrica;
    commentary = "South African university procurement shows limited transparency: 62% of tech procurements have undisclosed or unknown routes. Of the documented procurements, direct awards (22%) dominate, followed by in-house/OSS implementations (9%). Only 4% use competitive open tenders, with framework agreements and market soundings representing minimal shares—highlighting preference for bilateral arrangements over formal competitive processes.";
  } else {
    data = dataAustralia;
    commentary = "Most Australian university tech procurements are still made through sector panels and consortia (≈33%) or direct SaaS purchases (≈30%), while only a small share go through open tenders (≈1%) — highlighting a preference for streamlined, pre-approved procurement over competitive bidding.";
  }

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-[#00C2C2]" />
          <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('procurementRoutes')}</CardTitle>
        </div>
        <p className="text-sm text-[#5C6B7A]">{t('howInstitutionsBuy')}</p>
        {isSouthAfrica && (
          <p className="text-xs text-[#5C6B7A] italic mt-1">
            (Based on analysis of 260 total procurement records from South African universities)
          </p>
        )}
      </CardHeader>
      <CardContent className="p-3 md:p-6">
        <div className="h-[220px] md:h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(1)}%` : ''}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Custom legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 justify-center mt-3 mb-4">
          {data.map((entry, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <div 
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-[9px] md:text-[10px] text-[#0A1A2F] leading-tight">
                {entry.name}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-[#5C6B7A] leading-relaxed">
            {commentary}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}